import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import { getAll, update, revoke, setReleasePath, setTrustedContact, clearTrustedContact, isRevoked, migrateFromLocalStorage, resendInvitation } from '../services/beneficiaryService';
import { sendBeneficiaryNotification, sendTrustedContactNotification } from '../services/emailService';
import { getDistinctCategories } from '../services/vaultService';
import { getEnvelopeStatus, isTerminal } from '../services/transmissionService';
import TransmissionPreparationFlow from '../components/TransmissionPreparationFlow';
import ReleasePathSelector from '../components/ReleasePathSelector';
import TrustedContactForm, { CONTACT_SEND_FAILED_MESSAGE, normalizeTrustedContact } from '../components/TrustedContactForm';
import { DEFAULT_RELEASE_PATH, getReleasePath } from '../config/releasePathConfig';
import BeneficiaryRelationPicker, { RELATIONS } from '../components/BeneficiaryRelationPicker';
import ConfirmModal from '../components/ConfirmModal';
import PrioritySelector, { PRIORITY_CONFIG } from '../components/PrioritySelector';
import AttributionSelector from '../components/AttributionSelector';
import { TRANSMISSION_CONFIG } from '../config/transmissionConfig';

const S = '#00F5A0';
const G = '#8892A4';
const AMBER = '#F59E0B';

const STATUS_CONFIG = {
  pending:   { label: 'En attente', color: '#F59E0B' },
  confirmed: { label: 'Confirmé',   color: '#00F5A0' },
};

// D1 — libellé de la section Révoqués. Lignes antérieures à CP1 : revoked_reason
// et revoked_at restent NULL (jamais complétés rétroactivement, preuves K81) ;
// un refus du bénéficiaire s'y reconnaît à responded_at.
const revokedLabel = (b) => {
  const declined = b.revoked_reason === 'beneficiary_declined' || (!b.revoked_reason && !!b.responded_at);
  const date = b.revoked_at || b.responded_at;
  const base = declined ? 'Refusé par le bénéficiaire' : 'Révoqué';
  return date ? `${base} le ${new Date(date).toLocaleDateString('fr-FR')}` : base;
};

// CP3.2 : contact saisi dans la modale. Champs vides = aucun changement
// (le retrait passe par « Retirer le contact »).
const hasContactChanged = (draft, initial) => {
  const next = normalizeTrustedContact(draft);
  if (!next.name && !next.email) return false;
  return !initial || next.name !== initial.name || next.email !== initial.email;
};

const CARD = {
  background: 'rgba(255,255,255,0.04)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 16,
  padding: '20px',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const MAX_BENEFICIARIES_FREE = 3;

const BeneficiariesPage = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [resendFeedback, setResendFeedback] = useState({});
  // PF-03 CP2 : statut d'enveloppe par bénéficiaire actif, parcours de préparation.
  const [envelopeStatus, setEnvelopeStatus] = useState({});
  const [preparingTarget, setPreparingTarget] = useState(null);

  // Attribution state
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingState, setLoadingState] = useState('loading');
  const [editCategories, setEditCategories] = useState([]);
  const [editActExplicit, setEditActExplicit] = useState(false);
  const [editReleasePath, setEditReleasePath] = useState(DEFAULT_RELEASE_PATH);
  // PF-03 CP3.2 : contact de confiance (saisie modale, retrait, relance d'envoi).
  const [editContact, setEditContact] = useState(null);
  const [contactRemoveOpen, setContactRemoveOpen] = useState(false);
  const [contactFeedback, setContactFeedback] = useState({});

  const { isAuthenticated, user } = useAuth();
  const { masterKey } = useCrypto();
  const navigate = useNavigate();
  const migrationDone = useRef(false);

  const emptyForm = { firstName: '', lastName: '', email: '', phone: '', relation: null, priority: 1, message: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    (async () => {
      if (!migrationDone.current && masterKey && user) {
        migrationDone.current = true;
        await migrateFromLocalStorage(masterKey, user.id, user.email);
      }
      loadBeneficiaries();
      loadCategories();
    })();
  }, [isAuthenticated, masterKey, navigate, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBeneficiaries = async () => {
    setLoading(true);
    const result = await getAll(masterKey, { includeRevoked: true });
    if (result.success) {
      setBeneficiaries(result.data);
      loadEnvelopeStatuses(result.data.filter(b => !isRevoked(b)));
    } else setError(result.error);
    setLoading(false);
  };

  const loadEnvelopeStatuses = async (list) => {
    const entries = await Promise.all(list.map(async b => [b.id, await getEnvelopeStatus(b.id)]));
    setEnvelopeStatus(Object.fromEntries(entries));
  };

  // Relit la liste : enveloppe créée et, le cas échéant, contact et date de notification posés côté serveur.
  const handlePreparationDone = async () => {
    setPreparingTarget(null);
    await loadBeneficiaries();
  };

  const loadCategories = async () => {
    setLoadingState('loading');
    const result = await getDistinctCategories();
    if (result.success) {
      setAvailableCategories(result.data.filter(cat => TRANSMISSION_CONFIG[cat]));
      setLoadingState('ready');
    } else {
      setLoadingState('error');
    }
  };

  const openEdit = (b) => {
    const relation = b.relation || RELATIONS.find(r => r.label === b.relationship)?.id || null;
    const parts = (b.name || '').trim().split(/\s+/);
    const firstName = b.firstName || parts[0] || '';
    const lastName = b.lastName || parts.slice(1).join(' ') || '';
    setForm({ firstName, lastName, email: b.email, phone: b.phone || '', relation, priority: b.priority, message: b.message || '' });
    setEditCategories(b.access_categories ?? []);
    setEditActExplicit(false);
    setEditReleasePath(b.release_path || DEFAULT_RELEASE_PATH);
    setEditContact(b.trustedContact || null);
    setEditTarget(b); setError(''); setShowEditModal(true);
  };

  const closeModal = () => {
    setShowEditModal(false);
    setEditTarget(null);
    setError('');
    setEditCategories([]);
    setEditActExplicit(false);
    setEditContact(null);
  };

  // Dirty check for modal attribution — computed at render time
  const editInitialCats   = editTarget?.access_categories ?? [];
  const editDirty         = editTarget != null
    ? JSON.stringify([...editCategories].sort()) !== JSON.stringify([...editInitialCats].sort())
    : false;
  const editIsAttrRequired  = editDirty && editCategories.length > 0;
  const modalSubmitDisabled = submitting || (editIsAttrRequired && !editActExplicit);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim()) { setError("Le prénom, le nom, l'email et le téléphone sont obligatoires."); return; }
    setSubmitting(true);
    const name = `${form.firstName.trim()} ${form.lastName.trim()}`;
    const initialCats = editTarget?.access_categories ?? [];
    const dirty = JSON.stringify([...editCategories].sort()) !== JSON.stringify([...initialCats].sort());
    const payload = { ...form, name };
    if (dirty) payload.access_categories = editCategories;
    const result = await update(editTarget.id, payload, masterKey);
    if (!result.success) { setError(result.error); setSubmitting(false); return; }

    // update() ne relit pas le contact chiffré : on conserve celui déjà déchiffré.
    let updated = { ...result.data, trustedContact: editTarget.trustedContact ?? null };
    // Enregistrement partiel : ce qui est déjà en base est reflété, la modale reste ouverte sur l'erreur.
    const stopWithError = (message) => {
      setBeneficiaries(prev => prev.map(b => b.id === editTarget.id ? updated : b));
      setEditTarget(updated);
      setError(message);
      setSubmitting(false);
    };

    if (editReleasePath !== (editTarget.release_path || DEFAULT_RELEASE_PATH)) {
      const pathResult = await setReleasePath(editTarget.id, editReleasePath);
      if (!pathResult.success) { stopWithError(pathResult.error); return; }
      updated = { ...updated, release_path: editReleasePath };
    }

    const contactChanged = hasContactChanged(editContact, editTarget.trustedContact);
    if (contactChanged) {
      const contact = normalizeTrustedContact(editContact);
      const contactResult = await setTrustedContact(editTarget.id, contact, masterKey);
      if (!contactResult.success) { stopWithError(contactResult.error); return; }
      updated = { ...updated, trustedContact: contact };
      if (contactResult.emailChanged) {
        const sent = await sendTrustedContactNotification(editTarget.id, contact);
        if (!sent) { stopWithError(CONTACT_SEND_FAILED_MESSAGE); return; }
      }
    }

    setBeneficiaries(prev => prev.map(b => b.id === editTarget.id ? updated : b));
    closeModal();
    setSubmitting(false);
    // Hash et date de notification sont posés côté serveur : relecture.
    if (contactChanged) loadBeneficiaries();
  };

  const handleRevoke = (id) => {
    const b = beneficiaries.find(x => x.id === id);
    setRevokeTarget(b || { id });
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    const result = await revoke(revokeTarget.id);
    if (result.success) setBeneficiaries(prev => prev.map(b => b.id === revokeTarget.id ? { ...b, ...result.data } : b));
    else setError(result.error);
    setRevokeTarget(null);
  };

  const handleResend = async (b) => {
    setResendFeedback(prev => ({ ...prev, [b.id]: { loading: true } }));
    const result = await resendInvitation(b.id);
    if (!result.success) {
      setResendFeedback(prev => ({ ...prev, [b.id]: { error: result.error } }));
      return;
    }
    const ok = await sendBeneficiaryNotification(b, result.token, window.location.origin);
    if (!ok) {
      setResendFeedback(prev => ({ ...prev, [b.id]: { error: "L'invitation n'a pas pu être envoyée. Vérifiez votre connexion." } }));
    } else {
      setResendFeedback(prev => ({ ...prev, [b.id]: { success: true } }));
      setTimeout(() => setResendFeedback(prev => { const next = { ...prev }; delete next[b.id]; return next; }), 3000);
    }
  };

  const handleContactSend = async (b) => {
    setContactFeedback(prev => ({ ...prev, [b.id]: { loading: true } }));
    const sent = await sendTrustedContactNotification(b.id, b.trustedContact);
    if (!sent) {
      setContactFeedback(prev => ({ ...prev, [b.id]: { error: "L'email n'a pas pu être envoyé. Réessayez plus tard." } }));
      return;
    }
    setContactFeedback(prev => ({ ...prev, [b.id]: { success: true } }));
    setTimeout(() => setContactFeedback(prev => { const next = { ...prev }; delete next[b.id]; return next; }), 3000);
    loadBeneficiaries();
  };

  const confirmRemoveContact = async () => {
    setContactRemoveOpen(false);
    const result = await clearTrustedContact(editTarget.id);
    if (!result.success) { setError(result.error); return; }
    const cleared = {
      ...editTarget,
      trustedContact: null,
      trusted_contact_encrypted: null,
      trusted_contact_iv: null,
      trusted_contact_email_hash: null,
      trusted_contact_notified_at: null,
    };
    setBeneficiaries(prev => prev.map(b => b.id === cleared.id ? cleared : b));
    setEditTarget(cleared);
    setEditContact(null);
  };

  const activeBeneficiaries  = beneficiaries.filter(b => !isRevoked(b));
  const revokedBeneficiaries = beneficiaries.filter(isRevoked);
  const atLimit = activeBeneficiaries.length >= MAX_BENEFICIARIES_FREE;

  if (preparingTarget) {
    return (
      <TransmissionPreparationFlow
        beneficiary={preparingTarget}
        masterKey={masterKey}
        onDone={handlePreparationDone}
        onCancel={() => setPreparingTarget(null)}
      />
    );
  }

  if (loading && beneficiaries.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1628', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: S, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: G }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0A1628', color: '#fff', paddingTop: 80 }}>

      {/* ── Header ── */}
      <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ maxWidth: 1024, margin: '0 auto', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Mes bénéficiaires</h1>
            <p style={{ margin: '4px 0 0', color: G, fontSize: 14 }}>
              Personnes qui recevront vos consignes et accès en cas d'urgence
            </p>
          </div>
          <button onClick={() => !atLimit && navigate('/beneficiaries/add')} disabled={atLimit}
            style={{ background: atLimit ? 'rgba(255,255,255,0.05)' : S, color: atLimit ? G : '#0A1628', border: atLimit ? '1px solid rgba(255,255,255,0.08)' : 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: atLimit ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            + Ajouter un bénéficiaire
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1024, margin: '0 auto', padding: '32px 40px' }}>

        {/* ── Error ── */}
        {error && !showEditModal && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 24, color: '#f87171', fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* ── Priority explanation ── */}
        <div style={{ ...CARD, marginBottom: 20 }}>
          <p style={{ fontWeight: 600, margin: '0 0 10px', fontSize: 15 }}>Comment fonctionnent les priorités ?</p>
          <p style={{ margin: '0 0 14px', color: G, fontSize: 14, lineHeight: 1.7 }}>
            Lorsque votre check-in n'est pas effectué dans les délais, SafeHeir contacte vos bénéficiaires dans
            l'ordre de priorité. La <strong style={{ color: '#fff' }}>Priorité 1</strong> est alertée en premier.
            Si elle ne confirme pas sous 48 h, la <strong style={{ color: '#fff' }}>Priorité 2</strong> prend le relais,
            puis la <strong style={{ color: '#fff' }}>Priorité 3</strong> sous 48h supplémentaires si la P2 ne répond pas non plus.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[1, 2, 3].map(p => (
              <div key={p} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: PRIORITY_CONFIG[p].dot, display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}><strong>P{p}</strong> — {PRIORITY_CONFIG[p].text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── What a beneficiary receives ── */}
        <div style={{ background: 'rgba(0,245,160,0.03)', border: '1px solid rgba(0,245,160,0.1)', borderRadius: 14, padding: '18px 24px', marginBottom: 24 }}>
          <p style={{ fontWeight: 600, margin: '0 0 10px', fontSize: 14, color: S }}>Ce que reçoit un bénéficiaire</p>
          <ul style={{ margin: 0, paddingLeft: 20, color: G, fontSize: 13.5, lineHeight: 1.8 }}>
            <li>Un <strong style={{ color: '#fff' }}>email de notification</strong> expliquant la situation</li>
            <li>Les <strong style={{ color: '#fff' }}>consignes personnalisées</strong> que vous avez rédigées pour lui</li>
            <li>Un accès sécurisé aux informations que vous avez choisi de partager</li>
          </ul>
          <p style={{ fontSize: 12, fontStyle: 'italic', color: 'rgba(255,255,255,0.45)', marginTop: 12 }}>
            À venir avec SafeHeir Pro : alerte multi-canal — email + SMS + appel téléphonique vocal envoyés en simultané pour garantir la réception par le bénéficiaire.
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>
            SafeHeir ne transfère pas la propriété de vos comptes. Vous choisissez précisément quelles informations sont transmises.
          </p>
        </div>

        {/* ── Plan limit ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
          <p style={{ margin: 0, color: G, fontSize: 13 }}>
            Plan gratuit : {activeBeneficiaries.length} / {MAX_BENEFICIARIES_FREE} bénéficiaires
          </p>
          {atLimit && (
            <span style={{ background: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)', fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 20 }}>
              Limite atteinte — plan Pro prochainement
            </span>
          )}
        </div>

        {/* ── Empty state ── */}
        {activeBeneficiaries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', background: 'rgba(0,245,160,0.02)', borderRadius: 16, border: '1px dashed rgba(0,245,160,0.2)' }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>Aucun bénéficiaire configuré</h3>
            <p style={{ color: G, fontSize: 14, maxWidth: 380, margin: '0 auto 24px' }}>
              Ajoutez une personne de confiance qui recevra vos consignes et accès si vous ne pouvez plus répondre à vos check-ins.
            </p>
            <button onClick={() => navigate('/beneficiaries/add')}
              style={{ background: S, color: '#0A1628', border: 'none', borderRadius: 8, padding: '11px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
              Ajouter mon premier bénéficiaire
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {[...activeBeneficiaries].sort((a, b) => a.priority - b.priority).map((b) => {
              const cfg = PRIORITY_CONFIG[b.priority] || PRIORITY_CONFIG[3];
              return (
                <div key={b.id} style={{ ...CARD, position: 'relative' }}>
                  {/* Priority badge */}
                  <div style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.06)', borderRadius: 20, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 6, border: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>{cfg.label}</span>
                  </div>

                  {/* Identity */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, paddingRight: 90 }}>
                    <div style={{ width: 44, height: 44, background: 'rgba(0,245,160,0.08)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 16, fontWeight: 700, color: S }}>
                      {b.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{b.name}</div>
                      {(b.relation || b.relationship) && (
                        <div style={{ color: G, fontSize: 13 }}>
                          {b.relation
                            ? (RELATIONS.find(r => r.id === b.relation)?.label || b.relation)
                            : b.relationship}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact */}
                  <div style={{ fontSize: 13, color: G, marginBottom: 3 }}>{b.email}</div>
                  {b.phone && <div style={{ fontSize: 13, color: G, marginBottom: 3 }}>{b.phone}</div>}

                  {/* Status badge */}
                  {b.status && STATUS_CONFIG[b.status] && (
                    <div
                      aria-label={`Statut : ${STATUS_CONFIG[b.status].label}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        marginTop: 8,
                        background: `${STATUS_CONFIG[b.status].color}22`,
                        border: `1px solid ${STATUS_CONFIG[b.status].color}44`,
                        borderRadius: 6,
                        padding: '3px 8px',
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_CONFIG[b.status].color, display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_CONFIG[b.status].color }}>
                        {STATUS_CONFIG[b.status].label}
                      </span>
                    </div>
                  )}

                  {/* Attribution badge — only if categories are set */}
                  {b.access_categories?.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {b.access_categories.map(cat => (
                        <span key={cat} style={{ background: 'rgba(0,245,160,0.08)', color: S, padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                          {TRANSMISSION_CONFIG[cat]?.label || cat}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Transmission (PF-03 CP2) */}
                  {envelopeStatus[b.id] && (() => {
                    const st = envelopeStatus[b.id];
                    if (st.error) {
                      return <div style={{ marginTop: 8, fontSize: 12, color: G }}>Statut de transmission indisponible</div>;
                    }
                    const color = st.prepared ? S : AMBER;
                    const label = st.prepared ? 'Transmission préparée' : 'Transmission non préparée';
                    return (
                      <div style={{ marginTop: 8 }}>
                        <div
                          aria-label={`Transmission : ${label}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: `${color}22`, border: `1px solid ${color}44`, borderRadius: 6, padding: '3px 8px' }}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color }}>{label}</span>
                        </div>
                        {!isTerminal(b) && (
                          <div style={{ marginTop: 6, fontSize: 12, color: G }}>
                            Chemin : {(getReleasePath(b.release_path) || getReleasePath(DEFAULT_RELEASE_PATH)).label}
                          </div>
                        )}
                        {!st.prepared && (
                          <button onClick={() => setPreparingTarget(b)}
                            style={{ display: 'block', marginTop: 8, width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7, padding: '7px 0', fontSize: 13, color: '#e2e8f0', cursor: 'pointer', fontWeight: 500 }}>
                            Préparer la transmission
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Contact de confiance (PF-03 CP3.2) */}
                  {!isTerminal(b) && (() => {
                    const contact = b.trustedContact;
                    if (!contact && b.trusted_contact_email_hash) {
                      return <div style={{ marginTop: 8, fontSize: 12, color: '#f87171' }}>Contact de confiance illisible — retirez-le et saisissez-le à nouveau</div>;
                    }
                    if (!contact) {
                      return <div style={{ marginTop: 8, fontSize: 12, color: G }}>Aucun contact de confiance</div>;
                    }
                    const fb = contactFeedback[b.id];
                    return (
                      <div style={{ marginTop: 8, fontSize: 12, color: G }}>
                        {b.trusted_contact_notified_at ? (
                          `Contact de confiance : ${contact.name}, prévenu le ${new Date(b.trusted_contact_notified_at).toLocaleDateString('fr-FR')}`
                        ) : (
                          <>
                            Contact de confiance : {contact.name}, non prévenu{' '}
                            <button
                              type="button"
                              onClick={() => !fb?.loading && handleContactSend(b)}
                              disabled={!!fb?.loading}
                              style={{ background: 'none', border: 'none', padding: 0, color: S, fontSize: 12, fontWeight: 600, textDecoration: 'underline', cursor: fb?.loading ? 'not-allowed' : 'pointer' }}
                            >
                              {fb?.loading ? 'Envoi en cours...' : 'Envoyer'}
                            </button>
                          </>
                        )}
                        {fb?.success && <div style={{ marginTop: 4, color: S }}>Email envoyé</div>}
                        {fb?.error && <div style={{ marginTop: 4, color: '#f87171' }}>{fb.error}</div>}
                      </div>
                    );
                  })()}

                  {/* Message excerpt */}
                  {b.message && (
                    <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 10px', fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, maxHeight: 56, overflow: 'hidden' }}>
                      "{b.message}"
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(b)}
                      style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 7, padding: '8px 0', fontSize: 13, color: '#e2e8f0', cursor: 'pointer', fontWeight: 500 }}>
                      Modifier
                    </button>
                    <button onClick={() => handleRevoke(b.id)}
                      style={{ background: 'transparent', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 7, padding: '8px 12px', fontSize: 13, color: '#f87171', cursor: 'pointer' }}>
                      Révoquer
                    </button>
                  </div>

                  {b.status === 'pending' && (() => {
                    const fb = resendFeedback[b.id];
                    return (
                      <>
                        <button
                          onClick={() => !fb?.loading && handleResend(b)}
                          disabled={!!fb?.loading}
                          style={{ marginTop: 8, width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7, padding: '7px 0', fontSize: 13, color: G, cursor: fb?.loading ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                          {fb?.loading ? 'Envoi en cours...' : 'Renvoyer l\'invitation'}
                        </button>
                        {fb?.success && (
                          <p style={{ margin: '6px 0 0', fontSize: 12, color: S, textAlign: 'center' }}>Invitation renvoyée</p>
                        )}
                        {fb?.error && (
                          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#f87171', textAlign: 'center' }}>{fb.error}</p>
                        )}
                      </>
                    );
                  })()}

                  <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>
                    Ajouté le {new Date(b.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Revoked beneficiaries (D1) — lecture seule ── */}
        {revokedBeneficiaries.length > 0 && (
          <div style={{ marginTop: 40 }}>
            <SectionLabel>Bénéficiaires révoqués</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
              {revokedBeneficiaries.map((b) => (
                <div key={b.id} style={{ ...CARD, opacity: 0.55 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 44, height: 44, background: 'rgba(255,255,255,0.05)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: G }}>
                      <RevokedIcon />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{b.name}</div>
                      <div style={{ color: G, fontSize: 13 }}>{b.email}</div>
                    </div>
                  </div>
                  <div
                    aria-label={`Statut : ${revokedLabel(b)}`}
                    style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600, color: G }}
                  >
                    {revokedLabel(b)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm Revoke Modal (D1) ── */}
      <ConfirmModal
        open={!!revokeTarget}
        title="Révoquer ce bénéficiaire ?"
        message="Il ne recevra aucune transmission. La désignation est conservée à titre de preuve et cette action est définitive."
        confirmLabel="Révoquer"
        icon={<RevokeUserIcon />}
        onConfirm={confirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />

      {/* ── Confirm remove trusted contact (PF-03 CP3.2) ── */}
      <ConfirmModal
        open={contactRemoveOpen}
        danger={false}
        title="Retirer le contact de confiance ?"
        message="Ses coordonnées seront effacées. Aucun message ne lui sera envoyé."
        confirmLabel="Retirer"
        onConfirm={confirmRemoveContact}
        onCancel={() => setContactRemoveOpen(false)}
      />

      {/* ── Edit Modal ── */}
      {showEditModal && editTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: 'rgba(10,22,40,0.97)', backdropFilter: 'blur(20px)', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Modifier le bénéficiaire</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', color: G, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: 24 }}>
              {error && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 18, color: '#f87171', fontSize: 13 }}>
                  {error}
                </div>
              )}

              <SectionLabel>1 — Coordonnées</SectionLabel>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <Field label="Prénom *">
                    <input type="text" required value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="ex: Marie" style={inputStyle} autoComplete="given-name" />
                  </Field>
                </div>
                <div style={{ flex: 1 }}>
                  <Field label="Nom *">
                    <input type="text" required value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="ex: Dupont" style={inputStyle} autoComplete="family-name" />
                  </Field>
                </div>
              </div>
              <Field label="Adresse email *">
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="marie.dupont@email.com" style={inputStyle} />
              </Field>
              <Field label="Téléphone *">
                <input type="tel" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+33 6 12 34 56 78" style={inputStyle} />
              </Field>

              <SectionLabel style={{ marginTop: 20 }}>2 — Lien de parenté</SectionLabel>
              <Field label="Relation (optionnel)">
                <BeneficiaryRelationPicker
                  key={editTarget.id}
                  value={form.relation}
                  onChange={(id) => setForm(prev => ({ ...prev, relation: id }))}
                />
              </Field>

              <SectionLabel style={{ marginTop: 20 }}>3 — Ordre de contact</SectionLabel>
              <p style={{ fontSize: 13, color: G, margin: '0 0 12px', lineHeight: 1.5 }}>
                En cas de déclenchement, SafeHeir contacte d'abord la Priorité 1. Si elle ne répond pas sous 48 h, la Priorité 2 est alertée, puis la Priorité 3 sous 48h supplémentaires si la P2 ne répond pas non plus.
              </p>
              <Field label="Priorité">
                <PrioritySelector value={form.priority} onChange={n => setForm({ ...form, priority: n })} />
              </Field>

              <SectionLabel style={{ marginTop: 20 }}>4 — Message personnel (optionnel)</SectionLabel>
              <p style={{ fontSize: 13, color: G, margin: '0 0 12px', lineHeight: 1.5 }}>
                Ce texte sera joint à l'email de notification que recevra cette personne.
              </p>
              <Field label="Votre message">
                <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4}
                  placeholder="Si tu lis ce message, voici ce que j'aimerais que tu fasses…"
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }} />
              </Field>

              <SectionLabel style={{ marginTop: 20 }}>5 — Éléments à transmettre (optionnel)</SectionLabel>
              <p style={{ fontSize: 13, color: G, margin: '0 0 12px', lineHeight: 1.5 }}>
                Toute modification de l'attribution relance la journalisation de l'acte.
              </p>
              <div style={{ marginBottom: 4 }}>
                <AttributionSelector
                  availableCategories={availableCategories}
                  selectedCategories={editCategories}
                  actExplicit={editActExplicit}
                  onCategoriesChange={setEditCategories}
                  onActExplicitChange={setEditActExplicit}
                  loadingState={loadingState}
                  disabled={submitting}
                />
              </div>

              <SectionLabel style={{ marginTop: 20 }}>6 — Conservation du code de transmission</SectionLabel>
              <p style={{ fontSize: 13, color: G, margin: '0 0 12px', lineHeight: 1.5 }}>
                Chemin utilisé pour remettre le code de transmission de cette personne.
              </p>
              <div style={{ marginBottom: 4 }}>
                <ReleasePathSelector
                  value={editReleasePath}
                  onChange={setEditReleasePath}
                  disabled={submitting || isTerminal(editTarget)}
                />
              </div>

              <SectionLabel style={{ marginTop: 20 }}>7 — Contact de confiance</SectionLabel>
              <p style={{ fontSize: 13, color: G, margin: '0 0 12px', lineHeight: 1.5 }}>
                Un proche informé qu'une transmission est préparée, sans jamais recevoir le code ni aucune donnée du coffre.
                Un changement d'email lui envoie une nouvelle notification.
              </p>
              <TrustedContactForm
                key={`${editTarget.id}-${editTarget.trusted_contact_email_hash || 'aucun'}`}
                initial={editTarget.trustedContact}
                onChange={setEditContact}
                busy={submitting || isTerminal(editTarget)}
              />
              {editTarget.trusted_contact_email_hash && (
                <button type="button" onClick={() => setContactRemoveOpen(true)} disabled={submitting || isTerminal(editTarget)}
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, padding: '7px 12px', fontSize: 13, color: '#e2e8f0', cursor: 'pointer', marginBottom: 4 }}>
                  Retirer le contact
                </button>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <button type="submit" disabled={modalSubmitDisabled}
                  style={{ flex: 1, background: S, color: '#0A1628', border: 'none', borderRadius: 8, padding: '11px 0', fontWeight: 700, cursor: modalSubmitDisabled ? 'not-allowed' : 'pointer', fontSize: 14, opacity: modalSubmitDisabled ? 0.7 : 1 }}>
                  {submitting ? '…' : 'Enregistrer les modifications'}
                </button>
                <button type="button" onClick={closeModal}
                  style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '11px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionLabel = ({ children, style = {} }) => (
  <p style={{ fontWeight: 600, fontSize: 12, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px', ...style }}>
    {children}
  </p>
);

const RevokedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <line x1="5.7" y1="5.7" x2="18.3" y2="18.3" />
  </svg>
);

// K114 : révocation = désignation conservée, pas une suppression (pas de corbeille).
const RevokeUserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="17" y1="8" x2="23" y2="14" />
    <line x1="23" y1="8" x2="17" y2="14" />
  </svg>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6, fontWeight: 500 }}>{label}</label>
    {children}
  </div>
);

export default BeneficiariesPage;
